import '@widesk-ui/components/styles/TableBoxCollection.scss';

import ApiModel                 from '@widesk-core/models/ApiModel';
import Box                      from '@widesk-ui/components/Box';
import React                    from 'react';
import TableCollection          from '@widesk-ui/components/table/TableCollection';
import { TableCollectionProps } from '@widesk-ui/components/table/TableCollection';
import { ModelClass }           from '@mathquis/modelx/lib/types/collection';
import useTheme                 from '@widesk-ui/hooks/useTheme';
import { Action }               from '@widesk-ui/components/ButtonActions';
import TableStore               from '@widesk-ui/stores/TableStore';
import Input                    from '../form/Input';
import View                     from '../View';
import _debounce                from 'lodash/debounce';
import { ExpandAltOutlined }    from '@ant-design/icons';
import { SearchOutlined }       from '@ant-design/icons';
import { ShrinkOutlined }       from '@ant-design/icons';
import { autorun }              from 'mobx';
import Button                   from '@widesk-ui/components/Button';
import Tooltip                  from '@widesk-ui/components/Tooltip';

export type TableBoxCollectionProps<
	M extends ModelClass<ApiModel>,
> = Optional<TableCollectionProps<M>, 'fetch' | 'rowKey'> & {
	modelClass: M;
	title: React.ReactNode;
	inner?: boolean;
	variant?: 'borderless' | 'outlined';
	boxActions?: (tableStore: TableStore) => Action[];
	renderTopLeft?: () => React.ReactNode;
	renderTopRight?: () => React.ReactNode;
	translations?: LocaleHeaderValue;
	includeFiles?: boolean;
	showSearch?: boolean;
	expandAllRows?: boolean;
	expandAllRowsDefault?: 'expanded' | 'collapsed';
	collapsed?: boolean | {
		default: boolean;
	};
};

const TableBoxCollectionInner = function <M extends ModelClass<ApiModel>>(props: TableBoxCollectionProps<M>, ref?: React.ForwardedRef<TableStore>) {
	const { boxActions, expandAllRows, expandAllRowsDefault = 'expanded' } = props;

	const [actions, setActions] = React.useState<Action[]>();
	const [expandedRowKeys, setExpandedRowKeys] = React.useState<string[]>([]);
	const [allIds, setAllIds] = React.useState<string[]>([]);

	const tableStoreRef = React.useRef<TableStore>();
	const tableStore = tableStoreRef.current || null;

	if (ref && typeof ref === 'object') {
		ref.current = tableStore;
	}

	const theme = useTheme();

	// Permet de gérer le rafraichissement des actions avec mobx
	React.useEffect(() => {
		return autorun(() => setActions(boxActions && tableStoreRef.current ? boxActions(tableStoreRef.current) : undefined));
	}, [tableStoreRef.current, props.rowSelection?.selectedRowKeys]);

	const onFetchCollectionEnd = React.useCallback(async (coll: Parameters<NonNullable<TableCollectionProps<M>['onFetchCollectionEnd']>>[0]) => {
		if (expandAllRows) {
			const strIds = coll.ids.map((id: any) => id?.toString() || '');
			setAllIds(strIds);
			setExpandedRowKeys(expandAllRowsDefault === 'collapsed' ? [] : strIds);
		}
		return props.onFetchCollectionEnd?.(coll);
	}, [expandAllRows, props.onFetchCollectionEnd]);

	const rowExpandable = expandAllRows && props.rowExpandable ? {
		...props.rowExpandable,
		expandedRowKeys,
		onExpand: (expanded: boolean, record: any) => {
			const key = record.id?.toString() || '';
			setExpandedRowKeys(prev => expanded ? [...prev, key] : prev.filter(id => id !== key));
			props.rowExpandable?.onExpand?.(expanded, record);
		},
	} : props.rowExpandable;

	const allExpanded = allIds.length > 0 && expandedRowKeys.length === allIds.length;

	const renderTopRight = expandAllRows ? () => (
		<>
			{props.renderTopRight?.()}
			<Tooltip title={allExpanded ? 'Tout replier' : 'Tout déplier'}>
				<div>
					<Button onClick={() => allExpanded ? setExpandedRowKeys([]) : setExpandedRowKeys([...allIds])}>
						{allExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
					</Button>
				</div>
			</Tooltip>
		</>
	) : props.renderTopRight;

	return (
		<Box
			className="widesk-table-box-collection"
			cover={(
				<View heightF>
					{props.showSearch && (
						<View row align="center" marginT={10} paddingR={20} marginB={10} paddingL={20}>
							<Input
								placeholder="Rechercher"
								onChange={_debounce(async (value) => {
									await tableStore?.fetch(1, 0, { ...tableStore.sorts, search: value.target.value });
								}, 600)}
							/>
							<SearchOutlined style={{ position: 'absolute', right: 40, top: 75 }} />
						</View>
					)}

					<TableCollection
						{...props as unknown as TableBoxCollectionProps<M>}

						onFetchCollectionEnd={onFetchCollectionEnd}
						rowExpandable={rowExpandable}

						onDidMount={store => {
							tableStoreRef.current = store; // On récupère le store de Table dans notre composant

							if (props.onDidMount) {
								props.onDidMount(store);
							}
						}}

						pagination={props.pagination === false ? false : {
							pageSize: 5,
							pageSizeOptions: [5, 10, 25, 50],
							size: 'small',
							style: {
								paddingLeft: theme.marginLG,
								paddingRight: theme.marginLG,
							},
							...props.pagination,
						}}

						searchDisabled
					/>
				</View>
			)}

			{...props}
			renderTopRight={renderTopRight}
			actions={actions}
			size={props.size === 'small' ? 'small' : 'default'}
		/>
	);
};

const TableBoxCollection = React.forwardRef(TableBoxCollectionInner) as <M extends ModelClass<ApiModel>>(
	props: TableBoxCollectionProps<M> & { ref?: React.ForwardedRef<TableStore> },
) => ReturnType<typeof TableBoxCollectionInner>;

export default TableBoxCollection;
